import React from 'react';
import PropTypes from 'prop-types';

import Button from '../Button';
import MultilineText from '../MultilineText';
import { getCheckoutConfirmation } from './helpers';
import getButtonBackgroundColor from '../../lib/getButtonBackgroundColor';
import getButtonTextColor from '../../lib/getButtonTextColor';
import FilesList from '../FilesList/FilesList';
import request from '../../services/request';
import { apiUrl } from '../../services/env';
import downloadUrlAsFile from '../../services/downloadUrlAsFile';
import getSellerPortalUrl from '../../lib/getSellerPortalUrl';
import useCheckoutState from '../../hooks/useCheckoutState';
import { formatDateTime } from '@repo/checkout/lib/format-date';

const SubscriptionStatusMessage = ({
  subscription,
  email,
  status,
  checkout,
  seller,
  customer,
  preview,
  isConfirmation,
}) => {
  const { restartPayment } = useCheckoutState();

  switch (status) {
    case 'scheduled':
    case 'trialing':
    case 'active': {
      const { title, message, type } = getCheckoutConfirmation({
        checkout,
        subscription,
        seller,
        email,
      });

      const hasFiles = subscription.files && subscription.files.length > 0;

      return (
        <div className="cp_status-wrapper text-center p-4 w-full">
          <h2 className="cp_status-title text-black mb-2">
            <div
              className="flex justify-center mb-4 cp_status-icon"
              style={{
                color: seller.brandColor,
              }}
            >
              <svg width="56" height="56" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="m19 28 6 6 12-12m18 6a27 27 0 1 1-54 0 27 27 0 0 1 54 0Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {title}
          </h2>
          <div className="cp_status-content mb-4">
            {type === 'html' ? (
              <MultilineText
                className="cp_status-text"
                dangerouslySetInnerHTML={{
                  __html: message,
                }}
              />
            ) : (
              <MultilineText className="cp_status-text">{message}</MultilineText>
            )}
          </div>

          {hasFiles && checkout.showFilesList && (
            <div className="mb-8">
              <FilesList.List>
                {subscription.files?.map((file, index) => (
                  <FilesList.Item
                    key={file._id}
                    file={file}
                    isFirst={index === 0}
                    isPreview={preview}
                    onDownloadFile={async () => {
                      const response = await request(
                        apiUrl +
                          `/customers/${customer._id}/files/${file._id}/download?token=${customer.loginToken}`,
                      );

                      if (response?.data?.url) {
                        downloadUrlAsFile(response?.data?.url, file.name);
                      } else {
                        window.alert(
                          'Unable to download file. Please try again or contact support.',
                        );
                      }
                    }}
                  />
                ))}
              </FilesList.List>
            </div>
          )}

          {hasFiles && checkout.showViewDownloadsButton && (
            <div className="mb-4">
              <Button
                appearance="black"
                size="full"
                backgroundColor={getButtonBackgroundColor({
                  buttonBackgroundColor: checkout.buttonBackgroundColor,
                  brandColor: seller.brandColor,
                })}
                color={getButtonTextColor({
                  buttonTextColor: checkout.buttonTextColor,
                  buttonBackgroundColor: checkout.buttonBackgroundColor,
                  brandColor: seller.brandColor,
                })}
                handleClick={() => {
                  window.open(`${getSellerPortalUrl(seller)}/${customer.loginToken}`, '_blank');
                }}
              >
                {checkout.viewDownloadsText}
              </Button>
            </div>
          )}
        </div>
      );
    }
    case 'canceled': {
      const getCancelationMessage = () => {
        const cancelAt = subscription.cancelAt ? new Date(subscription.cancelAt) : null;
        const canceledAt = subscription.canceledAt ? new Date(subscription.canceledAt) : null;

        if (canceledAt) {
          const canceledAtDate = formatDateTime(canceledAt, {
            timezone: checkout.timezone,
            locale: checkout.locale,
          });
          return `Your subscription was canceled at ${canceledAtDate}.`;
        }

        if (cancelAt) {
          const cancelDate = formatDateTime(cancelAt, {
            timezone: checkout.timezone,
            locale: checkout.locale,
          });

          return `Your subscription will be canceled on ${cancelDate}.`;
        }

        return 'Your subscription has been canceled.';
      };

      return (
        <div className="cp_status-wrapper text-center p-4 w-full">
          <h2 className="cp_status-title text-black mb-2">Subscription canceled</h2>
          <div className="cp_status-content mb-4">{getCancelationMessage()}</div>
        </div>
      );
    }
    case 'failed':
    case 'unpaid':
      return (
        <div className="cp_status-wrapper text-center p-4 w-full">
          <h2 className="cp_status-title text-black mb-2">Payment failed</h2>
          <div className="cp_status-content mb-4">
            Your payment failed and has not been processed.
            <br />
            Please try again.
          </div>
          <Button
            handleClick={() => restartPayment({ isConfirmation })}
            appearance="black"
            className="cp_button"
            size="full"
            backgroundColor={getButtonBackgroundColor({
              buttonBackgroundColor: checkout.buttonBackgroundColor,
              brandColor: seller.brandColor,
            })}
            color={getButtonTextColor({
              buttonTextColor: checkout.buttonTextColor,
              buttonBackgroundColor: checkout.buttonBackgroundColor,
              brandColor: seller.brandColor,
            })}
          >
            Try again
          </Button>
        </div>
      );
    default:
      return (
        <div className="cp_status-wrapper text-center p-4 w-full">
          <h2 className="cp_status-title text-black mb-2">Something went wrong</h2>
          <div className="cp_status-content mb-4">Please contact support or try again.</div>
          <Button
            handleClick={() => restartPayment({ isConfirmation })}
            appearance="black"
            className="cp_button"
            size="full"
            backgroundColor={getButtonBackgroundColor({
              buttonBackgroundColor: checkout.buttonBackgroundColor,
              brandColor: seller.brandColor,
            })}
            color={getButtonTextColor({
              buttonTextColor: checkout.buttonTextColor,
              buttonBackgroundColor: checkout.buttonBackgroundColor,
              brandColor: seller.brandColor,
            })}
          >
            Try again
          </Button>
        </div>
      );
  }
};

SubscriptionStatusMessage.propTypes = {
  checkout: PropTypes.object,
  subscription: PropTypes.object,
  status: PropTypes.oneOf(['trialing', 'active', 'failed', 'canceled', 'unpaid']),
  restartPayment: PropTypes.func,
};

SubscriptionStatusMessage.defaultProps = {
  checkout: {},
  subscription: {},
  status: 'failed',
  restartPayment: null,
};

export default SubscriptionStatusMessage;
